/**
 * Vendor Dashboard — one page, three tabs (Overview / Referred Users /
 * Commissions). Fetches everything scoped to the authenticated vendor;
 * backend enforces data isolation.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, IndianRupee, TrendingUp, Clock, Copy, ExternalLink, LogOut, Loader2,
  Search, ChevronLeft, ChevronRight, ShieldAlert, ArrowUp, ArrowDown, Minus,
  User as UserIcon, Save, Landmark,
} from "lucide-react";
import { toast } from "sonner";
import {
  vendorMe, vendorDashboard, vendorReferrals, vendorCommissions, vendorLogout,
  vendorAnalyticsSummary, vendorPayouts,
  vendorGetProfile, vendorUpdateProfile,
} from "@/lib/vendorApi";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "analytics", label: "Analytics" },
  { key: "referrals", label: "Referred Users" },
  { key: "commissions", label: "Commissions" },
  { key: "payouts", label: "Payouts" },
  { key: "profile", label: "Profile" },
];

export default function VendorDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("overview");
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const meRes = await vendorMe();
        setMe(meRes);
        if (meRes?.vendor?.status !== "active") {
          setLoading(false);
          return;
        }
        const d = await vendorDashboard();
        setDash(d);
      } catch (err) {
        if (err?.response?.status === 401) {
          navigate("/vendor/login", { replace: true });
        } else {
          setError(err?.response?.data?.detail || "Failed to load dashboard.");
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const handleLogout = async () => {
    await vendorLogout();
    navigate("/vendor/login", { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0B0B] text-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0B0B0B] text-white p-8">
        <div className="max-w-md mx-auto rounded-2xl bg-red-500/10 border border-red-500/30 p-6 text-red-200 text-sm">
          {error}
        </div>
      </div>
    );
  }

  const vendor = me?.vendor;
  const pending = vendor?.status === "pending";
  const suspended = vendor?.status === "suspended";

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white">
      <Header vendor={vendor} onLogout={handleLogout} />

      <main className="max-w-6xl mx-auto px-6 py-8">
        {pending && (
          <PendingBanner />
        )}
        {suspended && (
          <SuspendedBanner />
        )}

        {vendor?.status === "active" && (
          <>
            <Tabs tab={tab} setTab={setTab} />

            {tab === "overview" && dash && (
              <Overview dash={dash} referralLink={me.referral_link} />
            )}
            {tab === "analytics" && (
              <AnalyticsTab />
            )}
            {tab === "referrals" && (
              <ReferralsTab />
            )}
            {tab === "commissions" && (
              <CommissionsTab />
            )}
            {tab === "payouts" && (
              <PayoutsTab />
            )}
            {tab === "profile" && (
              <ProfileTab />
            )}
          </>
        )}
      </main>
    </div>
  );
}

function Header({ vendor, onLogout }) {
  return (
    <header className="border-b border-white/8 bg-black/60 backdrop-blur-md sticky top-0 z-10">
      <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md bg-gradient-to-br from-[#F3CE5A] to-[#8B6914] flex items-center justify-center">
            <span className="font-display text-black text-xs font-bold">F</span>
          </div>
          <span className="font-display text-sm text-white tracking-tight">Facetra · Partner Portal</span>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="text-white/60" data-testid="vendor-header-name">{vendor?.business_name}</span>
          <button
            onClick={onLogout}
            data-testid="vendor-header-logout"
            className="text-white/50 hover:text-white transition inline-flex items-center gap-1.5"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </div>
    </header>
  );
}

function PendingBanner() {
  return (
    <div
      data-testid="vendor-pending-banner"
      className="mb-6 rounded-xl border border-amber-500/30 bg-amber-500/8 p-4 flex items-start gap-3"
    >
      <Clock className="w-5 h-5 text-amber-300 flex-shrink-0 mt-0.5" />
      <div>
        <p className="text-sm font-medium text-amber-200">Application under review</p>
        <p className="text-xs text-amber-200/70 mt-1 leading-relaxed">
          An admin will activate your partner account shortly. You'll get an email as soon as it's live.
        </p>
      </div>
    </div>
  );
}

function SuspendedBanner() {
  return (
    <div
      data-testid="vendor-suspended-banner"
      className="mb-6 rounded-xl border border-red-500/30 bg-red-500/8 p-4 flex items-start gap-3"
    >
      <ShieldAlert className="w-5 h-5 text-red-300 flex-shrink-0 mt-0.5" />
      <div>
        <p className="text-sm font-medium text-red-200">Account suspended</p>
        <p className="text-xs text-red-200/70 mt-1 leading-relaxed">
          Please contact support to reactivate your partner account.
        </p>
      </div>
    </div>
  );
}

function Tabs({ tab, setTab }) {
  return (
    <div className="flex gap-1 mb-6 border-b border-white/8" data-testid="vendor-tabs">
      {TABS.map((t) => (
        <button
          key={t.key}
          onClick={() => setTab(t.key)}
          data-testid={`vendor-tab-${t.key}`}
          className={`px-4 py-2.5 text-sm transition border-b-2 -mb-px ${
            tab === t.key
              ? "border-[#D4AF37] text-white"
              : "border-transparent text-white/50 hover:text-white/80"
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

function Overview({ dash, referralLink }) {
  const k = dash.kpis;
  const cards = [
    { label: "Referred Users", value: k.total_referred_users, icon: Users, testId: "vendor-kpi-users" },
    { label: "Active Subscribers", value: k.active_subscribers, icon: Users, testId: "vendor-kpi-active" },
    { label: "Revenue Generated", value: `₹${k.total_revenue_generated.toFixed(0)}`, icon: IndianRupee, testId: "vendor-kpi-revenue" },
    { label: "Total Commission", value: `₹${k.total_commission_earned.toFixed(0)}`, icon: TrendingUp, testId: "vendor-kpi-total-comm" },
    { label: "Paid Commission", value: `₹${k.paid_commission.toFixed(0)}`, icon: IndianRupee, testId: "vendor-kpi-paid" },
    { label: "Pending Commission", value: `₹${k.pending_commission.toFixed(0)}`, icon: Clock, testId: "vendor-kpi-pending" },
  ];

  return (
    <div className="space-y-6" data-testid="vendor-overview">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {cards.map((c) => (
          <div key={c.label} data-testid={c.testId} className="rounded-2xl border border-white/8 bg-white/[0.03] p-5">
            <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-wider">
              <c.icon className="w-3.5 h-3.5" /> {c.label}
            </div>
            <div className="mt-2.5 text-2xl font-display text-white tracking-tight">{c.value}</div>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-[#D4AF37]/20 bg-gradient-to-br from-[#D4AF37]/8 via-transparent to-transparent p-6">
        <p className="text-xs uppercase tracking-widest text-[#D4AF37]/80">Your referral link</p>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <code
            className="font-mono text-sm text-white bg-black/40 rounded-lg px-3 py-2 flex-1 min-w-0 truncate"
            data-testid="vendor-referral-link"
          >
            {referralLink}
          </code>
          <button
            onClick={() => {
              navigator.clipboard.writeText(referralLink);
              toast.success("Link copied");
            }}
            data-testid="vendor-referral-copy"
            className="rounded-lg bg-white/8 hover:bg-white/15 px-3 py-2 text-xs inline-flex items-center gap-1.5 transition"
          >
            <Copy className="w-3.5 h-3.5" /> Copy
          </button>
          <a
            href={referralLink}
            target="_blank"
            rel="noreferrer"
            data-testid="vendor-referral-open"
            className="rounded-lg bg-white/8 hover:bg-white/15 px-3 py-2 text-xs inline-flex items-center gap-1.5 transition"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Open
          </a>
        </div>
        <p className="text-xs text-white/45 mt-3">
          Commission rate: <span className="text-white/80">{dash.commission_rate}%</span> of the first purchase per referred customer.
        </p>
      </div>
    </div>
  );
}

// ============= Referrals tab =============
function ReferralsTab() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [data, setData] = useState({ items: [], total: 0 });
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const limit = 25;

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const d = await vendorReferrals({ q: q || undefined, status: status || undefined, limit, skip });
        setData(d);
      } finally {
        setLoading(false);
      }
    })();
  }, [q, status, skip]);

  useEffect(() => { setSkip(0); }, [q, status]);

  return (
    <div className="space-y-4" data-testid="vendor-referrals-tab">
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-3.5 h-3.5 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="search"
            placeholder="Search by name or email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="vendor-referrals-search"
            className="w-full pl-9 pr-3 py-2 bg-black/40 border border-white/10 rounded-lg text-sm text-white placeholder:text-white/30 focus:border-[#D4AF37]/60 focus:outline-none"
          />
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          data-testid="vendor-referrals-status"
          className="bg-black/40 border border-white/10 rounded-lg text-sm text-white px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none"
        >
          <option value="">All statuses</option>
          <option value="active">Active subscribers</option>
          <option value="inactive">Free / inactive</option>
        </select>
      </div>

      <div className="rounded-2xl border border-white/8 bg-white/[0.02] overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-white/50 text-xs uppercase tracking-wider">
              <th className="text-left px-4 py-3 font-normal">User</th>
              <th className="text-left px-4 py-3 font-normal">Email</th>
              <th className="text-left px-4 py-3 font-normal">Plan</th>
              <th className="text-left px-4 py-3 font-normal">Referred at</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={4} className="text-center py-8 text-white/40"><Loader2 className="w-4 h-4 inline animate-spin" /></td></tr>
            )}
            {!loading && data.items.length === 0 && (
              <tr><td colSpan={4} className="text-center py-8 text-white/40 text-xs">No referrals yet.</td></tr>
            )}
            {!loading && data.items.map((u) => (
              <tr key={u.user_id} data-testid={`vendor-ref-row-${u.user_id}`} className="border-t border-white/6 hover:bg-white/[0.02]">
                <td className="px-4 py-3">{u.name || "—"}</td>
                <td className="px-4 py-3 text-white/70">{u.email}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    u.plan && u.plan !== "free"
                      ? "bg-emerald-500/15 text-emerald-300"
                      : "bg-white/8 text-white/50"
                  }`}>
                    {u.plan || "free"}
                  </span>
                </td>
                <td className="px-4 py-3 text-white/50 text-xs">{u.referred_at ? new Date(u.referred_at).toLocaleDateString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Paginator total={data.total} skip={skip} setSkip={setSkip} limit={limit} testId="vendor-referrals-pager" />
    </div>
  );
}

// ============= Commissions tab =============
function CommissionsTab() {
  const [status, setStatus] = useState("");
  const [data, setData] = useState({ items: [], total: 0 });
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const limit = 25;

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const d = await vendorCommissions({ status: status || undefined, limit, skip });
        setData(d);
      } finally {
        setLoading(false);
      }
    })();
  }, [status, skip]);

  useEffect(() => { setSkip(0); }, [status]);

  return (
    <div className="space-y-4" data-testid="vendor-commissions-tab">
      <div className="flex flex-wrap gap-3 items-center">
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          data-testid="vendor-commissions-status"
          className="bg-black/40 border border-white/10 rounded-lg text-sm text-white px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none"
        >
          <option value="">All</option>
          <option value="pending">Pending payout</option>
          <option value="paid">Paid out</option>
        </select>
      </div>

      <div className="rounded-2xl border border-white/8 bg-white/[0.02] overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-white/50 text-xs uppercase tracking-wider">
              <th className="text-left px-4 py-3 font-normal">Order</th>
              <th className="text-right px-4 py-3 font-normal">Order ₹</th>
              <th className="text-right px-4 py-3 font-normal">Rate</th>
              <th className="text-right px-4 py-3 font-normal">Commission</th>
              <th className="text-left px-4 py-3 font-normal">Status</th>
              <th className="text-left px-4 py-3 font-normal">Date</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="text-center py-8 text-white/40"><Loader2 className="w-4 h-4 inline animate-spin" /></td></tr>
            )}
            {!loading && data.items.length === 0 && (
              <tr><td colSpan={6} className="text-center py-8 text-white/40 text-xs">No commissions yet.</td></tr>
            )}
            {!loading && data.items.map((c) => (
              <tr key={c.commission_id} data-testid={`vendor-comm-row-${c.commission_id}`} className="border-t border-white/6 hover:bg-white/[0.02]">
                <td className="px-4 py-3 text-white/70 font-mono text-xs">{c.order_id}</td>
                <td className="px-4 py-3 text-right">₹{c.order_amount?.toFixed(0)}</td>
                <td className="px-4 py-3 text-right text-white/60">{c.commission_rate}%</td>
                <td className="px-4 py-3 text-right font-medium">₹{c.commission_amount?.toFixed(0)}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    c.status === "paid"
                      ? "bg-emerald-500/15 text-emerald-300"
                      : "bg-amber-500/15 text-amber-300"
                  }`}>
                    {c.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-white/50 text-xs">{new Date(c.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Paginator total={data.total} skip={skip} setSkip={setSkip} limit={limit} testId="vendor-commissions-pager" />
    </div>
  );
}

function Paginator({ total, skip, setSkip, limit, testId }) {
  const page = Math.floor(skip / limit) + 1;
  const pages = Math.max(1, Math.ceil(total / limit));
  return (
    <div className="flex items-center justify-between text-xs text-white/40" data-testid={testId}>
      <span>Page {page} of {pages} · {total} total</span>
      <div className="flex gap-1">
        <button
          disabled={skip === 0}
          onClick={() => setSkip(Math.max(0, skip - limit))}
          className="p-1.5 rounded hover:bg-white/8 disabled:opacity-30"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <button
          disabled={skip + limit >= total}
          onClick={() => setSkip(skip + limit)}
          className="p-1.5 rounded hover:bg-white/8 disabled:opacity-30"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}


// ============= Analytics tab (Phase 2) =============
// User picked "tables + trend arrows, no charts" (option c) — so this
// tab is deliberately chart-free. KPI cards + a daily-breakdown table
// + funnel percentages give the same insight in a lighter UI.
function AnalyticsTab() {
  const [range, setRange] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const d = await vendorAnalyticsSummary({ rangeDays: range });
        if (!cancelled) setData(d);
      } catch (_e) {
        // vendor already active by parent gate — silent on read fail
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [range]);

  const funnel = data?.funnel;
  const trend = data?.trend;
  const current = data?.current;

  return (
    <div className="space-y-6" data-testid="vendor-analytics-tab">
      <div className="flex items-center gap-3">
        <label className="text-xs text-white/50">Range</label>
        <select
          value={range}
          onChange={(e) => setRange(Number(e.target.value))}
          data-testid="vendor-analytics-range"
          className="bg-black/40 border border-white/10 rounded-lg text-sm text-white px-3 py-1.5 focus:border-[#D4AF37]/60 focus:outline-none"
        >
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last 365 days</option>
        </select>
      </div>

      {loading && (
        <div className="text-center py-12 text-white/40"><Loader2 className="w-4 h-4 inline animate-spin" /></div>
      )}

      {!loading && data && (
        <>
          {/* KPI cards with trend arrows */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <TrendCard
              label="Referrals"
              value={current?.referrals ?? 0}
              deltaPct={trend?.referrals_delta_pct}
              testId="vendor-trend-referrals"
            />
            <TrendCard
              label="Commissions earned"
              value={`₹${(current?.commissions_earned ?? 0).toFixed(0)}`}
              deltaPct={trend?.commissions_delta_pct}
              testId="vendor-trend-commissions"
            />
            <TrendCard
              label="Revenue generated"
              value={`₹${(current?.revenue ?? 0).toFixed(0)}`}
              deltaPct={trend?.revenue_delta_pct}
              testId="vendor-trend-revenue"
            />
          </div>

          {/* Funnel */}
          {funnel && (
            <FunnelBoard funnel={funnel} />
          )}

          {/* Daily breakdown table (compact — last 30 rows) */}
          <DailyBreakdown series={data.time_series || []} />
        </>
      )}
    </div>
  );
}

function TrendCard({ label, value, deltaPct, testId }) {
  const arrow = deltaPct == null
    ? { Icon: Minus, cls: "text-white/45" }
    : deltaPct >= 0
      ? { Icon: ArrowUp, cls: "text-emerald-300" }
      : { Icon: ArrowDown, cls: "text-rose-300" };
  const label2 = deltaPct == null ? "no prior data" : `${Math.abs(deltaPct)}% vs prior period`;
  const Icon = arrow.Icon;
  return (
    <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-4" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-white/45">{label}</div>
      <div className="mt-1.5 flex items-baseline gap-2">
        <div className="font-display text-2xl text-white">{value}</div>
      </div>
      <div className={`mt-1 text-[11px] inline-flex items-center gap-1 ${arrow.cls}`}>
        <Icon className="w-3 h-3" /> {label2}
      </div>
    </div>
  );
}

function FunnelBoard({ funnel }) {
  const total = Math.max(1, funnel.referred || 1);
  const rows = [
    { label: "Clicked referral (signed up)", n: funnel.signed_up || 0 },
    { label: "Verified email", n: funnel.verified || 0 },
    { label: "First paid order", n: funnel.first_paid || 0 },
  ];
  return (
    <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-4" data-testid="vendor-funnel">
      <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-3">Attribution funnel</div>
      <div className="space-y-2.5">
        {rows.map((r) => {
          const pct = Math.round((r.n / total) * 100);
          return (
            <div key={r.label}>
              <div className="flex items-center justify-between text-xs text-white/70">
                <span>{r.label}</span>
                <span className="tabular-nums text-white/50">{r.n} <span className="text-white/30">· {pct}%</span></span>
              </div>
              <div className="mt-1 h-1.5 rounded-full bg-white/6 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#D4AF37] to-[#F3CE5A]"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DailyBreakdown({ series }) {
  // Show last 30 days regardless of range setting — keeps the table
  // readable. The KPI cards up top already reflect the selected range.
  const rows = (series || []).slice(-30).reverse();
  return (
    <div className="rounded-2xl border border-white/8 bg-white/[0.02] overflow-hidden">
      <div className="px-4 pt-3 pb-2 text-[10px] uppercase tracking-[0.22em] text-white/45">
        Daily breakdown (most recent 30)
      </div>
      <table className="w-full text-sm" data-testid="vendor-daily-breakdown">
        <thead>
          <tr className="text-white/50 text-xs uppercase tracking-wider">
            <th className="text-left px-4 py-2 font-normal">Date</th>
            <th className="text-right px-4 py-2 font-normal">Referrals</th>
            <th className="text-right px-4 py-2 font-normal">Commission ₹</th>
            <th className="text-right px-4 py-2 font-normal">Revenue ₹</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={4} className="text-center py-8 text-white/40 text-xs">No activity in this range yet.</td></tr>
          )}
          {rows.map((r) => (
            <tr key={r.date} className="border-t border-white/6">
              <td className="px-4 py-2 text-white/60">{r.date}</td>
              <td className="px-4 py-2 text-right tabular-nums">{r.referrals}</td>
              <td className="px-4 py-2 text-right tabular-nums">{r.commissions_earned.toFixed(0)}</td>
              <td className="px-4 py-2 text-right tabular-nums text-white/60">{r.revenue.toFixed(0)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============= Payouts tab (Phase 2) =============
function PayoutsTab() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const limit = 25;

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const d = await vendorPayouts({ limit, skip });
        setData(d);
      } finally {
        setLoading(false);
      }
    })();
  }, [skip]);

  return (
    <div className="space-y-4" data-testid="vendor-payouts-tab">
      <div className="rounded-2xl border border-white/8 bg-white/[0.02] overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-white/50 text-xs uppercase tracking-wider">
              <th className="text-left px-4 py-3 font-normal">Payout</th>
              <th className="text-left px-4 py-3 font-normal">Date</th>
              <th className="text-right px-4 py-3 font-normal">Commissions</th>
              <th className="text-right px-4 py-3 font-normal">Amount</th>
              <th className="text-left px-4 py-3 font-normal">Status</th>
              <th className="text-left px-4 py-3 font-normal">Reference</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="text-center py-8 text-white/40"><Loader2 className="w-4 h-4 inline animate-spin" /></td></tr>
            )}
            {!loading && data.items.length === 0 && (
              <tr><td colSpan={6} className="text-center py-8 text-white/40 text-xs">No payouts disbursed yet.</td></tr>
            )}
            {!loading && data.items.map((p) => (
              <tr key={p.payout_id} data-testid={`vendor-payout-row-${p.payout_id}`} className="border-t border-white/6 hover:bg-white/[0.02]">
                <td className="px-4 py-3 font-mono text-xs text-white/70">{p.payout_id}</td>
                <td className="px-4 py-3 text-white/50 text-xs">{new Date(p.created_at).toLocaleString()}</td>
                <td className="px-4 py-3 text-right tabular-nums">{p.commission_count}</td>
                <td className="px-4 py-3 text-right font-medium">₹{Number(p.total_amount).toFixed(0)}</td>
                <td className="px-4 py-3"><PayoutStatusBadge status={p.status} /></td>
                <td className="px-4 py-3 text-white/60 text-xs">{p.reference || <span className="text-white/30">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Paginator total={data.total} skip={skip} setSkip={setSkip} limit={limit} testId="vendor-payouts-pager" />
    </div>
  );
}

// ============= Payout status badge (iter 92) =============
// Shared between vendor + admin views. Colour-coded so the vendor
// immediately sees whether the money is coming, has landed, or bounced.
export function PayoutStatusBadge({ status }) {
  const s = (status || "sent").toLowerCase();
  const styles = {
    sent:     "bg-emerald-500/12 text-emerald-300 border-emerald-500/25",
    pending:  "bg-white/8 text-white/70 border-white/15",
    failed:   "bg-rose-500/12 text-rose-300 border-rose-500/25",
    refunded: "bg-amber-500/12 text-amber-300 border-amber-500/25",
  };
  const cls = styles[s] || styles.sent;
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${cls}`}
      data-testid={`payout-status-${s}`}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
      {s}
    </span>
  );
}


// ============= Profile tab (iter 93) =============
// Self-service form for the vendor's payout destination and tax info.
// GST is optional (per user 2026-02-08 spec). Bank account number is
// masked on display — full number lives server-side only.
function ProfileTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Current server-side view (bank number arrives already masked).
  const [server, setServer] = useState({
    upi_id: "",
    bank_account_holder: "",
    bank_account_number_masked: "",
    bank_ifsc: "",
    gst_number: "",
  });
  // Draft the user edits. `bank_account_number` in draft is either
  // empty (means "don't touch server-side value") OR the fresh full
  // number the user just typed (which we then send as-is on save).
  const [draft, setDraft] = useState({
    upi_id: "",
    bank_account_holder: "",
    bank_account_number: "",
    bank_ifsc: "",
    gst_number: "",
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await vendorGetProfile();
        if (cancelled) return;
        setServer(p);
        setDraft({
          upi_id: p.upi_id || "",
          bank_account_holder: p.bank_account_holder || "",
          bank_account_number: "",  // never pre-populate; masked-only
          bank_ifsc: p.bank_ifsc || "",
          gst_number: p.gst_number || "",
        });
      } catch (_e) {
        // Silent — form renders empty; save will still work.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const set = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));

  const dirty = (
    draft.upi_id !== (server.upi_id || "") ||
    draft.bank_account_holder !== (server.bank_account_holder || "") ||
    draft.bank_ifsc.toUpperCase() !== (server.bank_ifsc || "") ||
    draft.gst_number.toUpperCase() !== (server.gst_number || "") ||
    draft.bank_account_number.trim() !== ""
  );

  const save = async (e) => {
    e?.preventDefault?.();
    if (!dirty || saving) return;
    setSaving(true);
    // Only send fields the user actually touched. `undefined` in the
    // JSON is dropped by axios — the backend will leave those fields
    // untouched (per iter 93 contract).
    const patch = {
      upi_id: draft.upi_id.trim(),
      bank_account_holder: draft.bank_account_holder.trim(),
      bank_ifsc: draft.bank_ifsc.trim().toUpperCase(),
      gst_number: draft.gst_number.trim().toUpperCase(),
    };
    // Only include bank_account_number if the user typed a fresh one.
    if (draft.bank_account_number.trim()) {
      patch.bank_account_number = draft.bank_account_number.trim();
    }
    try {
      const res = await vendorUpdateProfile(patch);
      setServer(res);
      setDraft({
        upi_id: res.upi_id || "",
        bank_account_holder: res.bank_account_holder || "",
        bank_account_number: "",
        bank_ifsc: res.bank_ifsc || "",
        gst_number: res.gst_number || "",
      });
      toast.success("Profile saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save profile.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-16 text-white/40"><Loader2 className="w-5 h-5 inline animate-spin" /></div>;
  }

  return (
    <form onSubmit={save} className="max-w-2xl space-y-8" data-testid="vendor-profile-tab">
      {/* Payout destination */}
      <section className="rounded-2xl border border-white/8 bg-white/[0.02] p-5 space-y-5">
        <div className="flex items-center gap-2">
          <Landmark className="w-4 h-4 text-[#D4AF37]" />
          <h3 className="text-white text-sm font-medium">Payout destination</h3>
        </div>

        <ProfileField
          label="UPI VPA"
          hint="e.g. mehra.atelier@axl · fastest and free"
          value={draft.upi_id}
          onChange={set("upi_id")}
          placeholder="name@bank"
          testId="profile-upi"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ProfileField
            label="Account holder name"
            value={draft.bank_account_holder}
            onChange={set("bank_account_holder")}
            placeholder="Exactly as printed on the passbook"
            testId="profile-holder"
          />
          <ProfileField
            label="IFSC code"
            hint="11 chars — e.g. HDFC0000042"
            value={draft.bank_ifsc}
            onChange={set("bank_ifsc")}
            placeholder="ABCD0123456"
            className="uppercase"
            testId="profile-ifsc"
          />
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 block mb-1.5">
            Bank account number
          </label>
          {server.bank_account_number_masked && !draft.bank_account_number && (
            <div className="text-xs text-white/45 mb-1.5">
              On file: <span className="font-mono text-white/70">{server.bank_account_number_masked}</span> ·
              type a new number below to replace it.
            </div>
          )}
          <input
            type="text"
            inputMode="numeric"
            value={draft.bank_account_number}
            onChange={set("bank_account_number")}
            placeholder="9-18 digits · spaces and dashes are OK"
            data-testid="profile-account-number"
            className="w-full bg-[#141414] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#D4AF37]/60"
          />
        </div>
      </section>

      {/* Tax / compliance */}
      <section className="rounded-2xl border border-white/8 bg-white/[0.02] p-5 space-y-4">
        <div className="flex items-center gap-2">
          <UserIcon className="w-4 h-4 text-[#D4AF37]" />
          <h3 className="text-white text-sm font-medium">Tax &amp; compliance</h3>
        </div>
        <ProfileField
          label="GST number (optional)"
          hint="15-char GSTIN if you're registered. Skip if not — payouts still work."
          value={draft.gst_number}
          onChange={set("gst_number")}
          placeholder="27AAAPL1234C1ZK"
          className="uppercase"
          testId="profile-gst"
        />
      </section>

      <div className="flex items-center gap-3 sticky bottom-4">
        <button
          type="submit"
          disabled={!dirty || saving}
          data-testid="profile-save"
          className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-black text-sm font-medium px-6 py-3 hover:bg-[#F3CE5A] transition disabled:opacity-40 disabled:cursor-not-allowed shadow-[0_8px_30px_-8px_rgba(212,175,55,0.55)]"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Save profile
        </button>
        {dirty && !saving && (
          <span className="text-xs text-amber-300/80">You have unsaved changes.</span>
        )}
      </div>
    </form>
  );
}

function ProfileField({ label, hint, value, onChange, placeholder, className = "", testId }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 block mb-1.5">{label}</label>
      <input
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testId}
        className={`w-full bg-[#141414] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#D4AF37]/60 ${className}`}
      />
      {hint && <div className="text-[11px] text-white/40 mt-1.5">{hint}</div>}
    </div>
  );
}

